type RuntimeEnv = {
  apiUrl?: string;
  debug?: boolean;
  baseDomain?: string;     // optional manual override
  protocol?: 'http' | 'https';
};

declare global {
  interface Window {
    env?: RuntimeEnv;
  }
}

function getBaseDomainFromLocation(): string {
  if (typeof window === 'undefined') return 'zenit.sulinform.hu'; // SSR fallback

  const host = window.location.hostname; // no port here
  const knownServiceSubdomains = new Set([
    'idp', 'n8n', 'erp', 'avatars', 'forum', 'phpmyadmin', 'minio',
  ]);

  const parts = host.split('.');
  // If host is like "erp.zenit.sulinform.hu", strip the first label.
  if (parts.length >= 3 && knownServiceSubdomains.has(parts[0])) {
    return parts.slice(1).join('.');
  }

  // For localhost / IPs, you usually cannot derive a “base domain”
  // that supports subdomains, so rely on overrides.
  if (host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return 'zenit.sulinform.hu';
  }

  return host;
}

/*function getProtocol(): 'http' | 'https' {
  if (typeof window === 'undefined') return 'https';
  return (window.env?.protocol ?? (window.location.protocol.replace(':', '') as 'http' | 'https') ?? 'https');
}*/
function getProtocol(): 'http' | 'https' {
  if (typeof window === 'undefined') return 'https';

  // explicit override wins
  const override = window.env?.protocol;
  if (override === 'http' || override === 'https') return override;

  const p = window.location.protocol.replace(':', '');
  return (p === 'http' || p === 'https') ? p : 'https';
}


// Egyszemélyes mód: a window.env-ből, a be nem helyettesített "${...}" placeholder ellen védve.
function readStandalone(): boolean {
  const v = (window as any)?.env?.standalone;
  return v === true || v === 'true';
}
function readStandaloneLogin(): boolean {
  const v = (window as any)?.env?.standaloneLogin;
  return v === true || v === 'true';
}
function readStandaloneUser(): string {
  const v = (window as any)?.env?.standaloneUser;
  return (typeof v === 'string' && v && v[0] !== '$') ? v.trim().toLowerCase() : 'local-admin';
}
// Több-app matrix: a natív buildhez fixált alrendszer (üres = teljes app). Placeholder ('$') ellen védve.
function readFixedMode(): string {
  const v = (window as any)?.env?.fixedMode;
  return (typeof v === 'string' && v && v[0] !== '$') ? v.trim().toUpperCase() : '';
}

/**
 *  A window.env-ből érkező FELÜLÍRÁSOK biztonságos olvasása.
 *
 *  Az env.js sablonként települ (`"${ERP_OVERRIDE}"`), és ha a deploy nem helyettesíti be,
 *  a nyers helyőrző marad benne. Ezt nem szabad értéknek venni: a `${ERP_OVERRIDE}/api/...`
 *  RELATÍV útvonalként az oldal origójához oldódik fel, és a hívás 404-re fut
 *  (pl. https://erp.zenit.sulinform.hu/$%7BERP_OVERRIDE%7D/api/kerettanterv-sorrend/...).
 *  A dev környezetfájl eddig is védekezett ellene, a prod nem – innen jött a hibás link.
 *
 *  A "self" érték a megnyitott oldal origóját jelenti (tunnel/LAN-cím esetére).
 */
function ovr(nev: string): string {
  const v = (window as any)?.env?.[nev];
  if (typeof v !== 'string' || !v || v[0] === '$') return '';
  const t = v.trim();
  if (t.toLowerCase() === 'self') {
    return (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
  }
  return t.replace(/\/+$/, '');
}

const baseDomain = ovr('baseDomain') || getBaseDomainFromLocation();
const protocol = getProtocol();

const sub = (name: string) => `${protocol}://${name}.${baseDomain}`;

export const environment = {
  production: true,
  version: 'v2.0.8',
  /*  A FELÜLET SAJÁT VERZIÓJA – a képfájl építésekor kerül bele.
   *  Eddig a képernyőn látott verzió a KISZOLGÁLÓTÓL jött, a böngészőben futó
   *  felület viszont lehet régebbi (nyitva maradt fül, gyorsítótár). Emiatt egy
   *  frissítés után hiányzó menüpontot senki nem tudott mire vélni. Ez a szám
   *  arról szól, ami a böngészőben TÉNYLEG fut. */
  felulet_verzio: '2.3.321',
  apiUrl: ovr('apiUrl') || 'default',
  debug: window?.env?.debug || false,
  standalone: readStandalone(),
  standaloneLogin: readStandaloneLogin(),
  standaloneUser: readStandaloneUser(),
  fixedMode: readFixedMode(),

  // Központi intézmény/IdP-címtár worker (mobil pre-login választó) – FIX, nem intézményfüggő.
  // window.env.idpDirectory-vel felülírható; alapból a cronomit workers.dev host.
  idpDirectory: ovr('idpDirectory') || 'https://idp-worker.cronomit.org',

  // A host-ból derivált szolgáltatás-URL-ek. Natíven a választott intézmény FELÜLÍRHATJA őket
  // explicit URL-lel (ha a deploy nem a <sub>.<baseDomain> mintát követi – pl. dev tunnel, egyedi domain).
  keycloak: ovr('keycloakOverride') || sub('idp'),
  //  OIDC-issuer felülírás (Entra External ID / más OIDC IdP): teljes issuer + kliens-ID. Üresen a Keycloak marad.
  oidcIssuer: ovr('oidcIssuer') || '',
  oidcClientId: ovr('oidcClientId') || '',
  n8n: ovr('n8nOverride') || sub('n8n'),
  erp: ovr('erpOverride') || sub('erp'),
  avatars: ovr('avatarsOverride') || sub('avatars'),
  forum: ovr('forumOverride') || sub('forum'),
  apiOrigin: ovr('erpOverride') || sub('erp'),

  // NOTE: do not ship secrets in frontend bundles (see note below)
  s3access: 'OQNUEt5gh9kTIiUf6vh3',
  s3secret: 'amEPcKFQ8gWBVdAYoej0daD3HQz5VCIdUy2SPoDD',

  posapis: {
    sumup: 'https://api.sumup.com',
  },
};
